import { useCallback } from 'react';

import {
  MailAiReplyFieldsFragment,
  MailInsightFieldsFragment,
  useAnalyzeMailThreadMutation,
  useCreateTaskFromMailMutation,
  useMailAiDraftFetchQuery,
  useMailAiViewCountsCheckQuery,
  useMarkMailAiSuggestionMutation,
  useRewriteMailTextMutation,
} from 'core/graphql';

import { taskCounterRefetch } from '../Students/taskCounterQueries';
import { IMailResult, mailFailure } from './mailModel';
import { MailRewriteKey, rewriteEnum } from './mailAiModel';

export type IMailAiReply = MailAiReplyFieldsFragment;
export type IMailInsight = MailInsightFieldsFragment;
export type MailSuggestionOutcome = 'used' | 'edited' | 'dismissed';

/**
 * ТЗ 0122, «ИИ в почте» — готовый фоновый черновик треда (или `null`).
 * Без кеша Apollo: черновик живёт до первой вставки.
 */
export const useMailAiDraft = (threadId: string | null, enabled: boolean) => {
  const { data, loading } = useMailAiDraftFetchQuery({
    variables: { threadId: threadId || '' },
    skip: !enabled || !threadId,
    fetchPolicy: 'network-only',
  });
  return { draft: data?.mailAiDraft ?? null, loading };
};

/** Счётчики раздела «ИИ» левой колонки. */
export const useMailAiViewCounts = (
  mailboxIds: string[] | null,
  enabled: boolean,
) => {
  const { data, refetch } = useMailAiViewCountsCheckQuery({
    variables: { mailboxIds },
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
  });
  const reload = useCallback(() => {
    if (enabled) refetch().catch(() => undefined);
  }, [enabled, refetch]);
  return { counts: data?.mailAiViewCounts ?? null, reload };
};

const envelope = (
  result:
    | { ok: boolean; error?: string | null; errorCode?: string | null }
    | null
    | undefined,
): IMailResult =>
  result
    ? { ok: result.ok, error: result.error, errorCode: result.errorCode }
    : { ok: false, errorCode: 'UNAVAILABLE' };

/** Разбор, «улучшить текст», учёт черновиков ИИ. */
export const useMailAiActions = () => {
  const [analyzeMut, { loading: analyzing }] = useAnalyzeMailThreadMutation();
  const [rewriteMut, { loading: rewriting }] = useRewriteMailTextMutation();
  const [markMut] = useMarkMailAiSuggestionMutation();

  const analyze = useCallback(
    async (
      threadId: string,
      force = false,
    ): Promise<IMailResult & { insight?: IMailInsight | null }> => {
      try {
        const { data } = await analyzeMut({ variables: { threadId, force } });
        const result = data?.mailAiAnalyze;
        return { ...envelope(result), insight: result?.insight };
      } catch (caught) {
        return mailFailure(caught);
      }
    },
    [analyzeMut],
  );

  const rewrite = useCallback(
    async (
      mailboxId: string,
      html: string,
      mode: MailRewriteKey,
    ): Promise<
      IMailResult & { html?: string | null; suggestionId?: string | null }
    > => {
      try {
        const { data } = await rewriteMut({
          variables: { mailboxId, html, mode: rewriteEnum(mode) },
        });
        const result = data?.mailAiRewrite;
        return {
          ...envelope(result),
          html: result?.html,
          suggestionId: result?.suggestionId,
        };
      } catch (caught) {
        return mailFailure(caught);
      }
    },
    [rewriteMut],
  );

  /** Учёт черновика: ошибка учёта работе не мешает. */
  const markSuggestion = useCallback(
    (
      suggestionId: string | null | undefined,
      outcome: MailSuggestionOutcome,
    ) => {
      if (!suggestionId) return;
      markMut({ variables: { suggestionId, outcome } }).catch(() => undefined);
    },
    [markMut],
  );

  return { analyze, rewrite, markSuggestion, analyzing, rewriting };
};

export interface IMailTaskInput {
  threadId: string;
  title: string;
  description: string;
  assigneeId: string | null;
  dueDate: string | null;
  priority: string;
  linkEntities: boolean;
  markDone: boolean;
}

/** «Создать задачу» из письма — работает и без ИИ. */
export const useMailCreateTask = () => {
  const [createMut, { loading }] = useCreateTaskFromMailMutation();
  const create = useCallback(
    async (
      input: IMailTaskInput,
    ): Promise<IMailResult & { taskId?: string | null }> => {
      try {
        const { data } = await createMut({
          variables: {
            threadId: input.threadId,
            title: input.title,
            description: input.description || null,
            assigneeId: input.assigneeId || null,
            dueDate: input.dueDate || null,
            priority: input.priority,
            linkEntities: input.linkEntities,
            markDone: input.markDone,
          },
          // ТЗ 0129: новая задача (со сроком в прошлом — сразу просроченная)
          // перечитывает бейдж «Таск-трекера» и разбивку групп.
          refetchQueries: taskCounterRefetch(),
        });
        const result = data?.mailCreateTask;
        return { ...envelope(result), taskId: result?.taskId };
      } catch (caught) {
        return mailFailure(caught);
      }
    },
    [createMut],
  );
  return { create, creating: loading };
};
